/**
 * theme.js — palette and primitives.
 *
 * ─── Why this does NOT follow Telegram's themeParams ────────────────────────
 * The first version used var(--tg-bg, #080705) so the app would sit inside
 * the user's own Telegram theme. On a light-mode client that produced a white
 * background with black text and PolyEdge's amber floating on top — the
 * palette inverted and the product stopped looking like itself.
 *
 * PolyEdge is dark by identity, not by preference. The whole visual language
 * assumes it. So the background is forced, and only the header colour is
 * handed to Telegram so the chrome above the app matches.
 */

export const C = {
  bg0:      "#080705",
  bg1:      "#0d0b08",
  bg2:      "#12100c",
  ink:      "#e8d5b0",
  sub:      "#8a7f70",
  dim:      "#6b6155",
  amber:    "#f5a623",
  amberDim: "#c4841c",
  green:    "#7bc27e",
  red:      "#c4614a",
  cyan:     "#5fa8a0",
  border:   "rgba(245,166,35,0.18)",
  borderHi: "rgba(245,166,35,0.35)",
  mono:     "'IBM Plex Mono', ui-monospace, monospace",
  disp:     "'Bebas Neue', Impact, sans-serif",
};

export const money = (v, sign = true) => {
  const n = Number(v || 0);
  const s = sign && n >= 0 ? "+" : n < 0 ? "−" : "";
  return `${s}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const pnlColor = (v) =>
  Number(v || 0) > 0 ? C.green : Number(v || 0) < 0 ? C.red : C.sub;

export const shortAddr = (a) =>
  a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—";

export const Label = ({ children, style }) => (
  <div style={{ fontSize: 10, color: C.dim, letterSpacing: "0.14em",
                marginBottom: 7, ...style }}>
    {children}
  </div>
);

export const Panel = ({ children, style, accent, onClick }) => (
  <div
    onClick={onClick}
    role={onClick ? "button" : undefined}
    style={{
      border: `0.5px solid ${C.border}`, background: C.bg1,
      padding: "11px 12px",
      ...(accent ? { borderLeft: `2px solid ${accent}` } : {}),
      // touchAction and the transparent tap highlight matter on a webview:
      // without them taps feel laggy and flash a grey box.
      ...(onClick ? { cursor: "pointer", touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent" } : {}),
      ...style,
    }}
  >
    {children}
  </div>
);

export const Row = ({ label, value, color, last }) => (
  <div style={{ display: "flex", justifyContent: "space-between",
                padding: "10px 12px", gap: 10,
                borderBottom: last ? "none"
                  : `0.5px solid rgba(245,166,35,0.12)` }}>
    <span style={{ fontSize: 12, color: C.dim, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 12, color: color || C.ink, textAlign: "right",
                   overflowWrap: "anywhere" }}>{value}</span>
  </div>
);

export const Button = ({ children, onClick, variant = "primary", disabled, style }) => {
  const base = {
    padding: 13, width: "100%", fontFamily: C.mono, fontSize: 12,
    letterSpacing: "0.14em", textTransform: "uppercase",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.45 : 1,
    touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
    // 44px is the minimum comfortable touch target; a 13px-padded button
    // lands just under it without this.
    minHeight: 44,
  };
  const variants = {
    primary: { background: C.amber, color: "#080705", border: "none", fontWeight: 700 },
    ghost:   { background: "transparent", color: C.sub, border: `0.5px solid ${C.borderHi}` },
    danger:  { background: "transparent", color: C.red, border: `1px solid ${C.red}80` },
    quiet:   { background: "transparent", color: C.dim, border: `0.5px solid ${C.border}` },
  };
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
            style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
};

export const Segment = ({ options, value, onChange, style }) => (
  <div style={{ display: "grid",
                gridTemplateColumns: `repeat(${options.length},1fr)`,
                gap: 6, ...style }}>
    {options.map((o) => {
      const on = o.value === value;
      return (
        <div key={o.value} onClick={() => onChange(o.value)}
             style={{
               border: on ? `1px solid ${o.color || C.amber}`
                          : `0.5px solid ${C.border}`,
               background: on ? `${o.color || C.amber}14` : C.bg1,
               color: on ? (o.color || C.amber) : C.dim,
               padding: "10px 6px", textAlign: "center", fontSize: 11,
               letterSpacing: "0.08em", cursor: "pointer",
               touchAction: "manipulation",
               WebkitTapHighlightColor: "transparent",
             }}>
          {o.label}
        </div>
      );
    })}
  </div>
);

export const Empty = ({ children }) => (
  <div style={{ padding: "40px 20px", textAlign: "center", fontSize: 12.5,
                color: C.dim, lineHeight: 1.7 }}>
    {children}
  </div>
);

export const Chip = ({ children, color = C.dim }) => (
  <span style={{ fontSize: 10, color,
                 border: `1px solid ${color === C.dim ? C.border : color + "59"}`,
                 padding: "3px 8px", letterSpacing: "0.08em",
                 whiteSpace: "nowrap" }}>
    {children}
  </span>
);
